// Escala de calidad de red compartida por el mapa de cobertura, la tarjeta
// del detalle y la sección de red. Los umbrales copian a los del equipo y la
// nube (PDUI-LISTENER/netquality/scoring.py y
// PDUI-BACKEND/pdui_api/netquality/scoring.py): cada métrica se lleva a
// 0-100 y de ahí a un color, así el mapa pinta igual sea cual sea la métrica
// elegida (verde = bueno, rojo = malo).

const clamp = (v, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, v));

const linear = (good, bad) => (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return null;
  return clamp((100 * (Number(v) - bad)) / (good - bad));
};

const inverseLinear = (good, bad) => (q) => bad + (q / 100) * (good - bad);

const logSpeed = (full) => (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return null;
  if (Number(v) <= 0) return 0;
  return clamp((100 * Math.log10(1 + Number(v))) / Math.log10(1 + full));
};

const inverseLogSpeed = (full) => (q) => Math.pow(1 + full, q / 100) - 1;

export const INTERFACES = ["wifi", "lte", "ethernet"];

// Métricas que agrega la nube por celda (grid.METRICS). `quality` recibe el
// valor y la interfaz (la señal se evalúa distinto en WiFi y LTE).
export const METRICS = {
  score: {
    unit: "", decimals: 0, higherIsBetter: true,
    quality: (v) => (v === null || v === undefined ? null : clamp(Number(v))),
    inverse: (q) => q,
  },
  signal_dbm: {
    unit: "dBm", decimals: 0, higherIsBetter: true,
    quality: (v, iface) => (iface === "lte" ? linear(-80, -120)(v) : linear(-50, -90)(v)),
    inverse: (q, iface) => (iface === "lte" ? inverseLinear(-80, -120)(q) : inverseLinear(-50, -90)(q)),
    interfaces: ["wifi", "lte"],
  },
  rsrp: { unit: "dBm", decimals: 0, higherIsBetter: true, quality: linear(-80, -120), inverse: inverseLinear(-80, -120), interfaces: ["lte"] },
  rsrq: { unit: "dB", decimals: 0, higherIsBetter: true, quality: linear(-5, -20), inverse: inverseLinear(-5, -20), interfaces: ["lte"] },
  sinr: { unit: "dB", decimals: 0, higherIsBetter: true, quality: linear(20, -5), inverse: inverseLinear(20, -5), interfaces: ["lte"] },
  rtt_avg_ms: { unit: "ms", decimals: 0, higherIsBetter: false, quality: linear(40, 1000), inverse: inverseLinear(40, 1000) },
  jitter_ms: { unit: "ms", decimals: 0, higherIsBetter: false, quality: linear(5, 200), inverse: inverseLinear(5, 200) },
  packet_loss_pct: { unit: "%", decimals: 0, higherIsBetter: false, quality: linear(0, 20), inverse: inverseLinear(0, 20) },
  gw_rtt_ms: { unit: "ms", decimals: 0, higherIsBetter: false, quality: linear(5, 300), inverse: inverseLinear(5, 300) },
  down_mbps: { unit: "Mbps", decimals: 1, higherIsBetter: true, quality: logSpeed(10), inverse: inverseLogSpeed(10) },
  up_mbps: { unit: "Mbps", decimals: 1, higherIsBetter: true, quality: logSpeed(5), inverse: inverseLogSpeed(5) },
};

export const METRIC_ORDER = [
  "score", "signal_dbm", "rtt_avg_ms", "packet_loss_pct", "jitter_ms", "down_mbps", "up_mbps", "gw_rtt_ms", "rsrp", "rsrq", "sinr",
];

export function metricsFor(iface) {
  return METRIC_ORDER.filter((m) => !METRICS[m].interfaces || METRICS[m].interfaces.includes(iface));
}

export function metricQuality(metric, value, iface) {
  const spec = METRICS[metric];
  if (!spec) return null;
  return spec.quality(value, iface);
}

// Cinco paradas rojo -> verde, interpoladas en RGB.
const STOPS = [
  [0, [215, 48, 31]],
  [25, [252, 141, 89]],
  [50, [254, 224, 139]],
  [75, [166, 217, 106]],
  [100, [26, 152, 80]],
];

export function qualityColor(q) {
  if (q === null || q === undefined || Number.isNaN(Number(q))) return "#9ca3af";
  const v = clamp(Number(q));
  for (let i = 1; i < STOPS.length; i++) {
    const [q1, c1] = STOPS[i - 1];
    const [q2, c2] = STOPS[i];
    if (v <= q2) {
      const f = (v - q1) / (q2 - q1);
      const rgb = c1.map((c, k) => Math.round(c + (c2[k] - c) * f));
      return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
    }
  }
  return "rgb(26, 152, 80)";
}

export function gradeOf(score) {
  if (score === null || score === undefined) return null;
  if (score >= 80) return "A";
  if (score >= 60) return "B";
  if (score >= 40) return "C";
  if (score >= 20) return "D";
  return "E";
}

export function qualityLabelKey(q) {
  if (q === null || q === undefined) return "netmap.quality.unknown";
  if (q >= 80) return "netmap.quality.excellent";
  if (q >= 60) return "netmap.quality.good";
  if (q >= 40) return "netmap.quality.fair";
  if (q >= 20) return "netmap.quality.poor";
  return "netmap.quality.bad";
}

export function formatMetric(metric, value) {
  const spec = METRICS[metric];
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  const n = Number(value);
  const text = spec ? n.toFixed(spec.decimals) : String(n);
  return spec && spec.unit ? `${text} ${spec.unit}` : text;
}

// Paradas de la leyenda: calidad 100/75/50/25/0 con el valor de la métrica
// que corresponde a cada una (invirtiendo la escala).
export function legendStops(metric, iface) {
  const spec = METRICS[metric];
  if (!spec) return [];
  return [100, 75, 50, 25, 0].map((q) => ({
    q,
    color: qualityColor(q),
    value: metric === "score" ? q : spec.inverse(q, iface),
    label: metric === "score" ? String(q) : formatMetric(metric, spec.inverse(q, iface)),
  }));
}

export function interfaceLabel(iface) {
  return { wifi: "WiFi", lte: "LTE", ethernet: "Ethernet" }[iface] || iface;
}

// Redes que tiene el equipo, para los textos de ayuda: "Ethernet, WiFi y LTE"
// o, en un equipo sin WiFi, "Ethernet y LTE". `absent`: los medios que el
// equipo informa como present:false (absentMedia de current_connection_card).
const DEVICE_NETWORKS = ["ethernet", "wifi", "lte"];

export function networksText(absent, and) {
  let names = DEVICE_NETWORKS.filter((m) => !(absent || []).includes(m)).map(interfaceLabel);
  if (names.length === 0) names = DEVICE_NETWORKS.map(interfaceLabel);
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} ${and} ${names[names.length - 1]}`;
}
